import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import axiosInstance from '@/helpers/axios'
import { jwtDecode } from 'jwt-decode'
import { useRouter } from 'vue-router'

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter() // הוספת router כדי לנתב לאחר התנתקות
  const token = ref(null)
  const user = ref(null)
  const loading = ref(false)
  const initialCheckDone = ref(false) // פלאג לציון שהבדיקה הראשונית בוצעה

  const isAuthenticated = computed(() => !!user.value) // computed property לבדיקת אימות

  // פונקציה לעדכון הטוקן והמשתמש מ-accessToken
  function setAuthState(accessToken) {
    if (!accessToken) {
      token.value = null
      user.value = null
      axiosInstance.defaults.headers.common['Authorization'] = null
      return
    }
    try {
      token.value = accessToken
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
      const decoded = jwtDecode(accessToken) // פענוח הטוקן
      user.value = {
        id: decoded.userId,
        email: decoded.email,
        name: decoded.name,
        isAdmin: decoded.isAdmin
      }
      // console.log('🚀 ~ setAuthState ~ user:', user.value)
    } catch (error) {
      console.error('Error decoding token:', error)
      token.value = null
      user.value = null
      axiosInstance.defaults.headers.common['Authorization'] = null
    }
  }

  async function login(email, password) {
    loading.value = true
    try {
      const { data } = await axiosInstance.post('/api/auth/login', { email, password })
      setAuthState(data.accessToken) // עדכון הטוקן והמשתמש
      // console.log('Login successful')
      return true
    } catch (error) {
      // console.error('Login failed:', error)
      user.value = null
      token.value = null
      return false
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    try {
      await axiosInstance.post('/api/auth/logout')
      // console.log('Logout successful on server')
    } catch (error) {
      console.error('Logout failed:', error)
    } finally {
      token.value = null
      user.value = null
      axiosInstance.defaults.headers.common['Authorization'] = null
      initialCheckDone.value = true // עדכון הפלאג שהבדיקה הראשונית בוצעה
      // console.log('Logout successful on client')
      localStorage.removeItem('lastPath') // מחיקת הנתיב האחרון מה-localStorage
      router.push('/login') // ניתוב לדף הכניסה
    }
  }

  // פונקציה חדשה לקבלת פרטי משתמש, בודקת אם הטוקן תקין
  async function fetchUser() {
    loading.value = true
    if (!token.value) {
      // console.log('No token found, skipping fetchUser.')
      setAuthState(null)
      initialCheckDone.value = true // עדכון הפלאג שהבדיקה הראשונית בוצעה
      loading.value = false
      return false
    }
    try {
      // console.log('הבאת פרטי משתמש עם אסימון קיים ...')

      const { data } = await axiosInstance.get('/api/auth/me')
      user.value = data
      // console.log('🚀 ~ fetchUser ~ user:', user.value)
      initialCheckDone.value = true // עדכון הפלאג שהבדיקה הראשונית בוצעה
      return true
    } catch (error) {
      // אם הקריאה נכשלה, ה-interceptor של axios אמור לנסות לרענן,
      // אם הרענון נכשל, השגיאה תגיע לכאן
      console.error('Fetch user failed, likely need refresh or login:', error.response?.status)
      if (error.response?.status !== 401) {
        // שגיאה אחרת, לא קשורה לאימות בהכרח
        console.error('Non-auth error during fetchUser:', error)
      }
      setAuthState(null) // אם לא הצלחנו להביא את פרטי המשתמש, ננקה את הטוקן והמשתמש
      initialCheckDone.value = true // עדכון הפלאג שהבדיקה הראשונית בוצעה
      return false
    } finally {
      loading.value = false
    }
  }

  // רענון טוקן, נקרא ע"י ה-interceptor של axios אם קיבלנו שגיאה 401
  async function refreshToken() {
    // console.log('🚀 ~ refreshToken')
    loading.value = true
    try {
      const { data } = await axiosInstance.post('/api/auth/refresh_token')
      setAuthState(data.accessToken) // עדכון הטוקן והמשתמש
      // console.log('אסימון התרענן בהצלחה')
      return true
    } catch (error) {
      console.error('אסימון רענון נכשל:', error)
      token.value = null
      user.value = null
      axiosInstance.defaults.headers.common['Authorization'] = null
      initialCheckDone.value = true // עדכון הפלאג שהבדיקה הראשונית בוצעה
    } finally {
      loading.value = false
    }
  }

  /**
   * פונקציה לבדיקה ראשונית בעת טעינת האפליקציה
   * ניסיון לרענן את הטוקן והמשתמש
   * אם הרענון נכשל, ננקה את הטוקן והמשתמש
   * אם הרענון הצליח, ננסה להביא את פרטי המשתמש
   */
  async function attemptInitialAuth() {
    if (initialCheckDone.value) {
      console.log('Initial auth check already done, skipping.')
      return
    }
    // console.log('Attempting initial auth check...')
    loading.value = true
    let success = false
    try {
      const refreshSuccess = await refreshToken() // ניסיון לרענן את הטוקן
      if (refreshSuccess) {
        // אם הרענון הצליח, ננסה להביא את פרטי המשתמש
        await fetchUser()
        success = true
      } else {
        // אם הרענון נכשל, ננקה את הטוקן והמשתמש
        setAuthState(null)
      }
    } catch (error) {
      console.error('Error during initial auth check:', error)
      setAuthState(null) // אם קרתה שגיאה, ננקה את הטוקן והמשתמש;
    } finally {
      initialCheckDone.value = true // עדכון הפלאג שהבדיקה הראשונית בוצעה
      loading.value = false
      // console.log('בדיקת Auth ראשונית בוצעה. success:', success)
    }
  }

  return {
    token,
    user,
    loading,
    isAuthenticated,
    initialCheckDone,
    login,
    logout,
    refreshToken,
    fetchUser,
    attemptInitialAuth
  }
})
